import { useEffect } from "react";

import { prefetchRoute } from "../../screens/app/routeIntent";
import { toAppPath } from "../../shared/linkValidation";

// Long enough that sweeping the pointer across a menu doesn't prefetch every
// item on the way, short enough to finish well before a typical click.
export const HOVER_DELAY_MS = 100;

const LINK_SELECTOR = "a[href], [data-prefetch-href]";

const linkOf = (target) =>
  target instanceof Element ? target.closest(LINK_SELECTOR) : null;

// Buttons that navigate in a click handler opt in with data-prefetch-href.
const appPathOf = (el) => {
  if (!el) {
    return null;
  }
  if (el.dataset.prefetchHref) {
    return toAppPath(el.dataset.prefetchHref);
  }
  const opensElsewhere = el.target && el.target !== "_self";
  if (opensElsewhere || el.hasAttribute("download")) {
    return null;
  }
  return toAppPath(el.getAttribute("href"));
};

/**
 * Prefetches an in-app link's code and data when the user shows intent to
 * follow it: hovering it briefly, focusing it, or touching it. One listener on
 * the document covers every <Link>, <a> and data-prefetch-href element.
 */
export const useLinkIntentPrefetch = () => {
  useEffect(() => {
    let hovered = null;
    let timer = null;

    const cancelHover = () => {
      clearTimeout(timer);
      timer = null;
      hovered = null;
    };

    const onPointerOver = (e) => {
      if (e.pointerType !== "mouse") {
        return;
      }
      const el = linkOf(e.target);
      if (el === hovered) {
        return;
      }
      cancelHover();
      const path = appPathOf(el);
      if (path) {
        hovered = el;
        timer = setTimeout(() => prefetchRoute(path), HOVER_DELAY_MS);
      }
    };

    const onPointerOut = (e) => {
      if (hovered && !hovered.contains(e.relatedTarget)) {
        cancelHover();
      }
    };

    const prefetchNow = (e) => {
      const path = appPathOf(linkOf(e.target));
      if (path) {
        prefetchRoute(path);
      }
    };

    document.addEventListener("pointerover", onPointerOver);
    document.addEventListener("pointerout", onPointerOut);
    document.addEventListener("focusin", prefetchNow);
    document.addEventListener("touchstart", prefetchNow, { passive: true });
    return () => {
      cancelHover();
      document.removeEventListener("pointerover", onPointerOver);
      document.removeEventListener("pointerout", onPointerOut);
      document.removeEventListener("focusin", prefetchNow);
      document.removeEventListener("touchstart", prefetchNow);
    };
  }, []);
};
